import { CrossPlatformCourse } from './types';

export const CROSS_PLATFORM_COURSES: CrossPlatformCourse[] = [
  {
    id: 'flutter-dart-complete',
    title: 'Flutter & Dart for Mobile/Web/Desktop',
    description: 'Build beautiful, natively compiled applications for mobile, web, and desktop from a single codebase using Flutter and Dart.',
    level: 'intermediate',
    totalDuration: '12 hours',
    image: '/api/placeholder/400/300',
    tags: ['Flutter', 'Dart', 'Mobile Development', 'Cross-Platform', 'UI/UX'],
    prerequisites: ['Basic programming experience', 'Mobile development concepts helpful'],
    estimatedHours: 12,
    objectives: [
      'Build cross-platform apps with Flutter',
      'Master Dart programming language',
      'Create responsive UIs for all platforms',
      'Implement state management solutions'
    ],
    learningOutcomes: [
      'Develop apps for iOS, Android, web, and desktop',
      'Master Dart language and Flutter framework',
      'Create beautiful, responsive user interfaces',
      'Implement navigation and routing',
      'Manage application state effectively',
      'Connect to APIs and handle data',
      'Deploy to multiple app stores'
    ],
    modules: [
      {
        id: 'dart-fundamentals',
        title: 'Dart Programming Fundamentals',
        description: 'Learn the Dart programming language',
        content: 'Dart is a client-optimized language for fast apps on any platform...',
        duration: '2 hours',
        difficulty: 'beginner',
        order: 1,
        estimatedTime: '2 hours',
        learningObjectives: [
          'Understand Dart syntax and features',
          'Learn object-oriented programming in Dart',
          'Master async programming with Futures'
        ],
        codeExamples: [
          {
            title: 'Dart Class Example',
            code: 'class Person {\n  final String name;\n  final int age;\n  \n  Person(this.name, this.age);\n  \n  void introduce() {\n    print("Hi, I\'m $name and I\'m $age years old");\n  }\n}\n\nvoid main() {\n  final person = Person("Alice", 30);\n  person.introduce();\n}',
            explanation: 'A simple Dart class demonstrating constructor shorthand and string interpolation.',
            language: 'dart'
          }
        ]
      },
      {
        id: 'flutter-basics',
        title: 'Flutter Fundamentals',
        description: 'Get started with Flutter development',
        content: 'Flutter is Google\'s UI toolkit for building beautiful applications...',
        duration: '2.5 hours',
        difficulty: 'intermediate',
        order: 2
      },
      {
        id: 'flutter-widgets',
        title: 'Flutter Widgets and Layouts',
        description: 'Master Flutter\'s widget system',
        content: 'Everything in Flutter is a widget, from layouts to styling...',
        duration: '2.5 hours',
        difficulty: 'intermediate',
        order: 3
      },
      {
        id: 'flutter-navigation',
        title: 'Navigation and Routing',
        description: 'Implement app navigation patterns',
        content: 'Learn Navigator 2.0 and modern routing approaches...',
        duration: '2 hours',
        difficulty: 'intermediate',
        order: 4
      },
      {
        id: 'flutter-state-management',
        title: 'State Management',
        description: 'Manage application state effectively',
        content: 'Explore different state management solutions for Flutter...',
        duration: '2 hours',
        difficulty: 'advanced',
        order: 5
      },
      {
        id: 'flutter-platform-specific',
        title: 'Platform-Specific Development',
        description: 'Handle platform differences and native features',
        content: 'Learn to access native APIs and handle platform-specific code...',
        duration: '1 hour',
        difficulty: 'advanced',
        order: 6
      }
    ],
    type: 'cross-platform'
  },
  {
    id: 'tauri-desktop-applications',
    title: 'Tauri Desktop Applications with Rust',
    description: 'Build secure, fast, and lightweight desktop applications using Tauri framework with Rust backend and web frontend.',
    level: 'intermediate',
    totalDuration: '8 hours',
    image: '/api/placeholder/400/300',
    tags: ['Tauri', 'Rust', 'Desktop Development', 'Web Technologies', 'Security'],
    prerequisites: ['Basic Rust knowledge', 'HTML/CSS/JavaScript', 'Desktop app concepts'],
    estimatedHours: 8,
    objectives: [
      'Build desktop apps with Tauri',
      'Integrate Rust backend with web frontend',
      'Implement secure inter-process communication',
      'Package and distribute desktop applications'
    ],
    learningOutcomes: [
      'Create multi-platform desktop applications',
      'Use web technologies for desktop UI',
      'Implement secure Rust backend logic',
      'Handle file system operations safely',
      'Create native system integrations',
      'Package apps for different operating systems'
    ],
    modules: [
      {
        id: 'tauri-introduction',
        title: 'Introduction to Tauri',
        description: 'Learn Tauri architecture and setup',
        content: 'Tauri creates desktop apps with web frontends and Rust backends...',
        duration: '1.5 hours',
        difficulty: 'beginner',
        order: 1
      },
      {
        id: 'tauri-frontend-integration',
        title: 'Frontend Framework Integration',
        description: 'Connect React, Vue, or Svelte with Tauri',
        content: 'Learn to integrate modern web frameworks with Tauri...',
        duration: '2 hours',
        difficulty: 'intermediate',
        order: 2
      },
      {
        id: 'tauri-rust-commands',
        title: 'Rust Commands and IPC',
        description: 'Create Rust commands for frontend interaction',
        content: 'Implement secure communication between frontend and Rust backend...',
        duration: '2 hours',
        difficulty: 'intermediate',
        order: 3
      },
      {
        id: 'tauri-system-integration',
        title: 'System Integration and APIs',
        description: 'Access native system features',
        content: 'Learn to use system APIs, notifications, and file operations...',
        duration: '1.5 hours',
        difficulty: 'advanced',
        order: 4
      },
      {
        id: 'tauri-packaging-distribution',
        title: 'Packaging and Distribution',
        description: 'Build and distribute Tauri applications',
        content: 'Create installers and distribute apps across platforms...',
        duration: '1 hour',
        difficulty: 'intermediate',
        order: 5
      }
    ],
    type: 'cross-platform'
  },
  {
    id: 'advanced-react-native-patterns',
    title: 'Advanced React Native Patterns',
    description: 'Master advanced React Native patterns, performance optimization, and native module development.',
    level: 'advanced',
    totalDuration: '10 hours',
    image: '/api/placeholder/400/300',
    tags: ['React Native', 'Mobile Development', 'Performance', 'Native Modules', 'Advanced Patterns'],
    prerequisites: ['React Native experience', 'Native development basics', 'JavaScript/TypeScript'],
    estimatedHours: 10,
    objectives: [
      'Implement advanced React Native patterns',
      'Optimize performance for complex apps',
      'Create custom native modules',
      'Handle complex navigation scenarios'
    ],
    learningOutcomes: [
      'Build high-performance React Native apps',
      'Create reusable component libraries',
      'Implement complex animations and gestures',
      'Develop custom native modules',
      'Optimize bundle size and runtime performance',
      'Handle offline functionality and data sync'
    ],
    modules: [
      {
        id: 'rn-advanced-architecture',
        title: 'Advanced Architecture Patterns',
        description: 'Structure large React Native applications',
        content: 'Learn scalable architecture patterns for complex mobile apps. This module covers clean architecture, MVVM, MVC, and reactive patterns using Redux, MobX, and Zustand for state management in large-scale applications.',
        duration: '2.5 hours',
        difficulty: 'advanced',
        order: 1,
        quiz: {
          questions: [
            {
              id: 'rn-arch-q1',
              question: 'Which pattern is best for managing global state in large React Native apps?',
              options: [
                'Props drilling',
                'Context API with reducers',
                'Redux or Zustand with middleware',
                'Local component state only'
              ],
              correctAnswer: 2,
              difficulty: 'medium',
              explanation: 'Redux or Zustand with middleware provides the most scalable solution for complex state management with time-travel debugging and predictable state updates.',
              category: 'architecture',
              points: 15
            },
            {
              id: 'rn-arch-q2',
              question: 'What is the primary benefit of using MVVM architecture in React Native?',
              options: [
                'Faster rendering',
                'Separation of business logic from UI',
                'Smaller bundle size',
                'Better animations'
              ],
              correctAnswer: 1,
              difficulty: 'medium',
              explanation: 'MVVM separates presentation logic (ViewModel) from UI components, making code more testable and maintainable.',
              category: 'architecture',
              points: 15
            },
            {
              id: 'rn-arch-q3',
              question: 'Which approach helps prevent prop drilling in deeply nested components?',
              options: [
                'Using more props',
                'Context API or state management libraries',
                'Inline styles',
                'Component duplication'
              ],
              correctAnswer: 1,
              difficulty: 'easy',
              explanation: 'Context API and state management libraries like Redux allow components to access state without passing props through every level.',
              category: 'architecture',
              points: 10
            },
            {
              id: 'rn-arch-q4',
              question: 'What is a key principle of clean architecture?',
              options: [
                'UI components should contain business logic',
                'Dependencies should point inward toward business rules',
                'All code should be in one file',
                'State should always be global'
              ],
              correctAnswer: 1,
              difficulty: 'hard',
              explanation: 'Clean architecture mandates that dependencies point inward, making business logic independent of frameworks and UI.',
              category: 'architecture',
              points: 20
            },
            {
              id: 'rn-arch-q5',
              question: 'Which pattern is recommended for handling side effects in Redux?',
              options: [
                'Inline in reducers',
                'Redux Thunk or Redux Saga middleware',
                'Direct API calls in components',
                'Global variables'
              ],
              correctAnswer: 1,
              difficulty: 'medium',
              explanation: 'Redux middleware like Thunk or Saga handles async operations and side effects outside of reducers, keeping them pure.',
              category: 'architecture',
              points: 15
            }
          ],
          passThreshold: 85,
          timeLimit: 600,
          category: 'architecture'
        },
        practice: {
          task: 'Implement a scalable Redux store with async middleware for a social media feed feature',
          hints: [
            'Create separate slices for users, posts, and comments',
            'Use Redux Toolkit for simplified setup',
            'Implement thunks for API calls',
            'Add error handling and loading states'
          ]
        }
      },
      {
        id: 'rn-performance-optimization',
        title: 'Performance Optimization',
        description: 'Optimize React Native app performance',
        content: 'Master techniques for improving app performance and user experience. Learn about React.memo, useMemo, useCallback, FlatList optimization, image caching, bundle size reduction, and profiling tools to create lightning-fast mobile applications.',
        duration: '2.5 hours',
        difficulty: 'advanced',
        order: 2,
        quiz: {
          questions: [
            {
              id: 'rn-perf-q1',
              question: 'Which hook prevents unnecessary re-renders by memoizing expensive calculations?',
              options: ['useState', 'useEffect', 'useMemo', 'useContext'],
              correctAnswer: 2,
              difficulty: 'easy',
              explanation: 'useMemo memoizes computed values and only recalculates when dependencies change, preventing expensive recalculations.',
              category: 'performance',
              points: 10
            },
            {
              id: 'rn-perf-q2',
              question: 'What is the recommended component for rendering long lists in React Native?',
              options: ['ScrollView', 'FlatList or SectionList', 'View with map()', 'ListView'],
              correctAnswer: 1,
              difficulty: 'easy',
              explanation: 'FlatList and SectionList use virtualization to render only visible items, dramatically improving performance for long lists.',
              category: 'performance',
              points: 10
            },
            {
              id: 'rn-perf-q3',
              question: 'How can you prevent unnecessary re-renders of child components?',
              options: [
                'Use inline functions for props',
                'React.memo and stable callback references',
                'Add more state',
                'Remove all props'
              ],
              correctAnswer: 1,
              difficulty: 'medium',
              explanation: 'React.memo prevents re-renders when props haven\'t changed, and useCallback creates stable function references.',
              category: 'performance',
              points: 15
            },
            {
              id: 'rn-perf-q4',
              question: 'Which tool helps identify performance bottlenecks in React Native?',
              options: [
                'console.log',
                'React DevTools Profiler and Flipper',
                'Alert dialogs',
                'Debugger statements'
              ],
              correctAnswer: 1,
              difficulty: 'medium',
              explanation: 'React DevTools Profiler and Flipper provide detailed performance metrics and help identify slow renders and memory leaks.',
              category: 'performance',
              points: 15
            },
            {
              id: 'rn-perf-q5',
              question: 'What technique reduces JavaScript bundle size in React Native?',
              options: [
                'Adding more dependencies',
                'Hermes engine and code splitting',
                'Removing all comments',
                'Using inline styles everywhere'
              ],
              correctAnswer: 1,
              difficulty: 'hard',
              explanation: 'Hermes engine optimizes bytecode, and code splitting with dynamic imports loads code on-demand, reducing initial bundle size.',
              category: 'performance',
              points: 20
            }
          ],
          passThreshold: 85,
          timeLimit: 600,
          category: 'performance'
        },
        practice: {
          task: 'Optimize a slow-performing FlatList with images by implementing proper memoization and lazy loading',
          hints: [
            'Use React.memo on list item components',
            'Implement getItemLayout for better performance',
            'Add image caching with react-native-fast-image',
            'Use initialNumToRender and maxToRenderPerBatch props'
          ]
        }
      },
      {
        id: 'rn-animations-gestures',
        title: 'Advanced Animations and Gestures',
        description: 'Create smooth animations and complex gestures',
        content: 'Use Reanimated 2 and Gesture Handler for advanced interactions. Master worklets, shared values, spring animations, gesture-driven animations, and create buttery-smooth 60fps experiences with native performance.',
        duration: '2 hours',
        difficulty: 'advanced',
        order: 3,
        quiz: {
          questions: [
            {
              id: 'rn-anim-q1',
              question: 'Which library provides the best performance for complex animations in React Native?',
              options: [
                'Animated API',
                'React Native Reanimated 2',
                'CSS animations',
                'setTimeout loops'
              ],
              correctAnswer: 1,
              difficulty: 'easy',
              explanation: 'Reanimated 2 runs animations on the UI thread using worklets, achieving 60fps performance without blocking JavaScript.',
              category: 'animations',
              points: 10
            },
            {
              id: 'rn-anim-q2',
              question: 'What are worklets in React Native Reanimated?',
              options: [
                'Worker threads',
                'JavaScript functions that run on UI thread',
                'CSS classes',
                'Animation presets'
              ],
              correctAnswer: 1,
              difficulty: 'medium',
              explanation: 'Worklets are JavaScript functions marked with \'worklet\' directive that can run on the UI thread for smooth animations.',
              category: 'animations',
              points: 15
            },
            {
              id: 'rn-anim-q3',
              question: 'Which hook creates gesture-driven animations in Reanimated?',
              options: [
                'useState',
                'useAnimatedGestureHandler',
                'useEffect',
                'useContext'
              ],
              correctAnswer: 1,
              difficulty: 'medium',
              explanation: 'useAnimatedGestureHandler processes gesture events and updates shared values directly on the UI thread.',
              category: 'animations',
              points: 15
            },
            {
              id: 'rn-anim-q4',
              question: 'What is the purpose of useSharedValue in Reanimated?',
              options: [
                'Share data between components',
                'Create values that can be read/written on UI thread',
                'Store global state',
                'Cache API responses'
              ],
              correctAnswer: 1,
              difficulty: 'hard',
              explanation: 'useSharedValue creates mutable values accessible on both JS and UI threads, perfect for smooth animations.',
              category: 'animations',
              points: 20
            },
            {
              id: 'rn-anim-q5',
              question: 'Which animation type creates natural, physics-based motion?',
              options: [
                'withTiming',
                'withSpring',
                'Linear interpolation',
                'Step animation'
              ],
              correctAnswer: 1,
              difficulty: 'easy',
              explanation: 'withSpring creates physics-based animations with bounce and damping, feeling more natural than linear timing.',
              category: 'animations',
              points: 10
            }
          ],
          passThreshold: 85,
          timeLimit: 600,
          category: 'animations'
        },
        practice: {
          task: 'Create a swipeable card component with spring animations and gesture handling',
          hints: [
            'Use PanGestureHandler from react-native-gesture-handler',
            'Implement useAnimatedGestureHandler for smooth gestures',
            'Add withSpring for natural card release animation',
            'Use useAnimatedStyle to animate transform and opacity'
          ]
        }
      },
      {
        id: 'rn-native-modules',
        title: 'Custom Native Modules',
        description: 'Develop custom native functionality',
        content: 'Create native modules for iOS and Android platforms. Learn to bridge native Swift/Objective-C and Java/Kotlin code, expose native APIs to JavaScript, handle callbacks and promises, and leverage platform-specific features not available in React Native core.',
        duration: '2 hours',
        difficulty: 'expert',
        order: 4,
        quiz: {
          questions: [
            {
              id: 'rn-native-q1',
              question: 'What is the primary purpose of native modules in React Native?',
              options: [
                'Improve JavaScript performance',
                'Access platform-specific APIs not available in React Native',
                'Replace React components',
                'Handle routing'
              ],
              correctAnswer: 1,
              difficulty: 'easy',
              explanation: 'Native modules bridge the gap between JavaScript and native code, exposing platform-specific features to React Native.',
              category: 'native',
              points: 10
            },
            {
              id: 'rn-native-q2',
              question: 'Which decorator is used to expose iOS methods to React Native?',
              options: [
                '@Override',
                '@ReactMethod',
                '@IBAction',
                '@Published'
              ],
              correctAnswer: 1,
              difficulty: 'medium',
              explanation: '@ReactMethod (or RCT_EXPORT_METHOD) marks methods as callable from JavaScript in React Native.',
              category: 'native',
              points: 15
            },
            {
              id: 'rn-native-q3',
              question: 'How do native modules handle asynchronous operations?',
              options: [
                'Using setTimeout',
                'Promises and callbacks',
                'Inline execution only',
                'Global variables'
              ],
              correctAnswer: 1,
              difficulty: 'medium',
              explanation: 'Native modules use promises (resolve/reject) or callbacks to handle async operations and return results to JavaScript.',
              category: 'native',
              points: 15
            },
            {
              id: 'rn-native-q4',
              question: 'What is TurboModules in React Native?',
              options: [
                'A faster JavaScript engine',
                'New architecture for lazy-loading native modules',
                'Animation library',
                'State management tool'
              ],
              correctAnswer: 1,
              difficulty: 'hard',
              explanation: 'TurboModules is part of the new architecture, enabling lazy initialization and type-safe native module interfaces.',
              category: 'native',
              points: 20
            },
            {
              id: 'rn-native-q5',
              question: 'Which file defines the interface for a native module in the new architecture?',
              options: [
                'package.json',
                'NativeModule spec (JavaScript/TypeScript)',
                'AndroidManifest.xml',
                'Info.plist'
              ],
              correctAnswer: 1,
              difficulty: 'hard',
              explanation: 'The new architecture uses JavaScript/TypeScript spec files to define type-safe interfaces for native modules using Codegen.',
              category: 'native',
              points: 20
            }
          ],
          passThreshold: 85,
          timeLimit: 600,
          category: 'native'
        },
        practice: {
          task: 'Create a native module that accesses device battery level on both iOS and Android',
          hints: [
            'Implement RCTBridgeModule protocol for iOS',
            'Extend ReactContextBaseJavaModule for Android',
            'Use @ReactMethod for both platforms',
            'Return battery percentage via Promise',
            'Handle permissions properly'
          ]
        }
      },
      {
        id: 'rn-offline-sync',
        title: 'Offline Functionality and Data Sync',
        description: 'Handle offline scenarios and data synchronization',
        content: 'Implement robust offline functionality with data sync. Master NetInfo API, AsyncStorage, SQLite databases, conflict resolution strategies, optimistic updates, and build apps that work seamlessly online and offline.',
        duration: '1 hour',
        difficulty: 'advanced',
        order: 5,
        quiz: {
          questions: [
            {
              id: 'rn-offline-q1',
              question: 'Which API detects network connectivity changes in React Native?',
              options: [
                'navigator.onLine',
                '@react-native-community/netinfo',
                'fetch with timeout',
                'Bluetooth API'
              ],
              correctAnswer: 1,
              difficulty: 'easy',
              explanation: '@react-native-community/netinfo provides real-time network state detection for React Native apps.',
              category: 'offline',
              points: 10
            },
            {
              id: 'rn-offline-q2',
              question: 'What is the recommended storage solution for offline data in React Native?',
              options: [
                'LocalStorage',
                'AsyncStorage or SQLite',
                'Cookies',
                'Session storage'
              ],
              correctAnswer: 1,
              difficulty: 'easy',
              explanation: 'AsyncStorage provides key-value storage, while SQLite offers relational database capabilities for complex offline data.',
              category: 'offline',
              points: 10
            },
            {
              id: 'rn-offline-q3',
              question: 'What is optimistic updating in offline-first apps?',
              options: [
                'Always assuming network is available',
                'Updating UI immediately before server confirmation',
                'Only updating when online',
                'Caching all API responses'
              ],
              correctAnswer: 1,
              difficulty: 'medium',
              explanation: 'Optimistic updates immediately reflect changes in UI while queuing sync operations, improving perceived performance.',
              category: 'offline',
              points: 15
            },
            {
              id: 'rn-offline-q4',
              question: 'How should you handle conflicts when syncing offline changes?',
              options: [
                'Always use client data',
                'Conflict resolution strategies (last-write-wins, merge, manual)',
                'Ignore conflicts',
                'Always use server data'
              ],
              correctAnswer: 1,
              difficulty: 'hard',
              explanation: 'Proper conflict resolution requires strategies like timestamps, version vectors, or user intervention for critical data.',
              category: 'offline',
              points: 20
            },
            {
              id: 'rn-offline-q5',
              question: 'Which library provides offline-first data synchronization with GraphQL?',
              options: [
                'Redux',
                'Apollo Client with offline capabilities',
                'Axios',
                'Fetch API'
              ],
              correctAnswer: 1,
              difficulty: 'medium',
              explanation: 'Apollo Client offers built-in offline support with cache persistence and automatic conflict resolution.',
              category: 'offline',
              points: 15
            }
          ],
          passThreshold: 85,
          timeLimit: 600,
          category: 'offline'
        },
        practice: {
          task: 'Build an offline-capable todo app with sync queue and conflict resolution',
          hints: [
            'Use NetInfo to detect connectivity',
            'Store todos in AsyncStorage or SQLite',
            'Implement a sync queue for pending operations',
            'Add timestamp-based conflict resolution',
            'Show sync status indicators in UI'
          ]
        }
      }
    ],
    type: 'cross-platform'
  }
];